import { Step as ModelStep } from "@redotech/redo-return-core-common/return-flow";
import { triggerFromModel } from "@redotech/redo-return-core-common/return-flow-step-from-model";
import {
  isTriggerStepValid,
  TriggerState,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { TriggerType } from "@redotech/redo-return-core-common/return-flow/trigger-enums";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { produce } from "immer";
import * as React from "react";
import { StepDownstream, StepType, StepTypeDetailsProps } from "../step";

export function triggerTypeLabel(type: TriggerType | string) {
  return type.charAt(0).toUpperCase() + type.slice(1).replaceAll("_", " ");
}

export const TRIGGER = (
  Details: React.ComponentType<StepTypeDetailsProps<TriggerState>>,
): StepType<TriggerState, ModelStep.Trigger> => ({
  Details: (props: StepTypeDetailsProps<TriggerState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return triggerTypeLabel(state.triggerType);
  },
  displayTitle(state) {
    return triggerTypeLabel(state.triggerType);
  },
  valid(state) {
    return isTriggerStepValid(state).valid;
  },
  title: STEP_TYPE_NAMES[ModelStep.TRIGGER],
  Icon: () => null,
  description() {
    return "";
  },
  start(state) {
    return state.next;
  },
  empty: { triggerType: TriggerType.ORDER_CREATED, next: undefined },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(`Invalid handle index ${handleIndex} for Trigger step`);
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  layout() {
    return BlockLayout.COMPACT;
  },
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      type: ModelStep.TRIGGER,
      triggerType: state.triggerType,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      next: id(state.next!),
    };
  },
  fromModel(model, id) {
    return triggerFromModel(model, id);
  },
});
